<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-pencil-square me-2"></i>Edit Supplier
      </h4>
      <div>
        <button class="btn btn-outline-secondary" @click="$router.go(-1)">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading supplier data...</p>
      </div>

      <div v-if="!loading" class="supplier-form-content fade-in">
        <form @submit.prevent="">
          <!-- Basic Information Section -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3">
              <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Basic Information</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="form-group col-md-3">
                  <label for="code" class="form-label fw-bold">Supplier Code:</label>
                  <input type="text" class="form-control" placeholder="Code" v-model="supplier.code" readonly>
                  <small class="text-muted">Auto-generated code</small>
                </div>
                <div class="form-group col-md-5">
                  <label for="name" class="form-label fw-bold">Name:</label>
                  <input type="text" class="form-control" placeholder="Supplier name" v-model="supplier.name">
                </div>
                <div class="form-group col-md-4">
                  <label for="status" class="form-label fw-bold">Status:</label>
                  <select data="" class="form-select" v-model="supplier.status">
                    <option value="" disabled>Select status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label for="type" class="form-label fw-bold">Type:</label>
                  <select data="" class="form-select" v-model="supplier.type">
                    <option value="" disabled>Select type</option>
                    <option value="Distributor">Distributor</option>
                    <option value="Wholesaler">Wholesaler</option>
                    <option value="Manufacturer">Manufacturer</option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label for="email" class="form-label fw-bold">Email:</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-envelope"></i></span>
                    <input type="email" class="form-control" placeholder="Email address" v-model="supplier.email">
                  </div>
                </div>
                <div class="form-group col-md-4">
                  <label for="phone" class="form-label fw-bold">Phone:</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-telephone"></i></span>
                    <input type="text" class="form-control" placeholder="Phone number" v-model="supplier.phone">
                  </div>
                </div>
                <div class="form-group col-md-12">
                  <label for="address" class="form-label fw-bold">Address:</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-geo-alt"></i></span>
                    <input type="text" class="form-control" placeholder="Physical address" v-model="supplier.address">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Banking Information Section -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3">
              <h5 class="mb-0"><i class="bi bi-bank me-2"></i>Banking Information <span class="text-muted small">(Optional)</span></h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="form-group col-md-6">
                  <label for="account_name" class="form-label fw-bold">Account Name:</label>
                  <input type="text" class="form-control" placeholder="Account name" v-model="supplier.account_name">
                </div>
                <div class="form-group col-md-6">
                  <label for="account_no" class="form-label fw-bold">Account Number:</label>
                  <input type="text" class="form-control" placeholder="Account number" v-model="supplier.account_no">
                </div>
                <div class="form-group col-md-6">
                  <label for="bank_name" class="form-label fw-bold">Bank Name:</label>
                  <input type="text" class="form-control" list="bankOptions" id="bankName" v-model="supplier.bank_name" placeholder="Type to search...">
                  <datalist id="bankOptions">
                    <option v-for="bank in bankNames.banks" :key="bank" :value="bank"></option>
                  </datalist>
                </div>
                <div class="form-group col-md-6">
                  <label for="branch_name" class="form-label fw-bold">Branch Name:</label>
                  <input type="text" class="form-control" list="branchOptions" id="branchName" v-model="supplier.branch_name" placeholder="Type to search...">
                  <datalist id="branchOptions">
                    <option v-for="branch in bankBranches" :key="branch" :value="branch"></option>
                  </datalist>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="d-flex justify-content-center gap-3 mt-4 mb-3">
            <button class="btn btn-outline-secondary px-4" @click="$router.go(-1)">
              <i class="bi bi-arrow-left me-1"></i> Cancel
            </button>
            <button v-if="profile.role !== 'user'" class="btn btn-primary px-4" @click="submitForm">
              <i class="bi bi-check-lg me-1"></i> Update Supplier
            </button>
          </div>
        </form>
      </div>
    </div>
  </Card>
</template>

<script>
import supplierService from "@/api/supplierService";
import bankService from "@/api/bankService";
import {decrypt} from "@/utils/crypto";
import loadingButton from "@/utils/loadingButton";

export default {
  name: "EditSupplier",

  data() {
    return {
      profile: {},
      supplier: {},
      loading: true,
      bankNames: {
        banks: [],
        branches: {}
      },
      bankBranches: []
    }
  },

  methods: {
    submitForm: async function (e) {
      try {
        loadingButton(e);
        await supplierService.updateSupplier(this.supplier, this.$route.params.id);
        this.$router.push('/supplier/' + this.$route.params.id);
      } catch (error) {
        console.error("Error updating supplier:", error);
      } finally {
        loadingButton(e, false);
      }
    },
  },

  async created() {
    try {
      this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
      this.supplier = await supplierService.getSupplier(this.$route.params.id);
      this.bankNames = await bankService.getBankNames();

      // Initialize bankBranches if bank_name is already set
      if (this.supplier.bank_name) {
        this.bankBranches = this.bankNames.branches[this.supplier.bank_name] || [];
      }
    } catch (error) {
      console.error("Error in created lifecycle hook:", error);
    } finally {
      this.loading = false;
    }
  },

  watch: {
    'supplier.bank_name': function () {
      this.bankBranches = this.bankNames.branches[this.supplier.bank_name] || [];
    }
  },
}
</script>

<style scoped>
/* Form styles */
.supplier-form-content {
  padding: 0.5rem;
}

.form-label {
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.card {
  transition: all 0.3s ease;
  overflow: hidden;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05) !important;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .input-group-text {
    padding: 0.25rem 0.5rem;
  }
}
</style>
